/**
 * Results Panel — Score summary, route list, map viewer.
 * Supports multiple result tiers; user picks which to view.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import * as api from '../services/routeOptimizerService';
import MapViewer from './MapViewer';
import { useRouteData, DayTabs, RouteCards } from './RouteList';
import ItineraryTable from './ItineraryTable';
import { ROUTE_COLORS, TIER_ORDER, TIER_LABELS } from '../utils/constants';

export default function ResultsPanel({ weekId, settings, resultTiers, addresses, onProceed }) {
  const [activeTier, setActiveTier] = useState(null);
  const [results, setResults] = useState(null);
  const [coords, setCoords] = useState(null);
  const [geometries, setGeometries] = useState(null);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [activeDay, setActiveDay] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tierSummary, setTierSummary] = useState({});
  const [runHistory, setRunHistory] = useState([]);

  // Sort available tiers
  const sortedTiers = useMemo(() =>
    (resultTiers || []).slice().sort((a, b) => TIER_ORDER.indexOf(a) - TIER_ORDER.indexOf(b)),
    [resultTiers]
  );

  // Load tier summary to determine best tier, and run history for delta
  useEffect(() => {
    if (sortedTiers.length < 2) return;
    api.getResultsSummary(weekId)
      .then(data => setTierSummary(data.tiers || {}))
      .catch(() => {});
  }, [weekId, sortedTiers.length]);

  useEffect(() => {
    if (!weekId) return;
    api.getRunHistory(weekId).then(h => setRunHistory(h || [])).catch(() => {});
  }, [weekId]);

  const bestTier = useMemo(() => {
    const available = TIER_ORDER.filter(t => tierSummary[t]);
    if (available.length < 2) return null;
    return available.reduce((best, t) =>
      Number(tierSummary[t].total_distance_miles) < Number(tierSummary[best].total_distance_miles) ? t : best
    );
  }, [tierSummary]);

  // Auto-select best tier (last in order = longest budget = usually best)
  useEffect(() => {
    if (sortedTiers.length > 0 && !activeTier) {
      setActiveTier(sortedTiers[sortedTiers.length - 1]);
    }
  }, [sortedTiers, activeTier]);

  // Load results for the active tier
  useEffect(() => {
    if (!activeTier) { setLoading(false); return; }
    setLoading(true);
    const load = async () => {
      try {
        const [res, geo, geom] = await Promise.all([
          api.getResults(weekId, activeTier),
          api.getGeocode(weekId),
          api.getGeometries(weekId, activeTier).catch(() => null),
        ]);
        setResults(res);
        setCoords(geo);
        setGeometries(geom);
        setSelectedRoute(null);
        setActiveDay(null);
      } catch (err) {
        console.error('Failed to load results:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [weekId, activeTier]);

  const techColors = useMemo(() => {
    const colors = {};
    if (!results?.solution?.routes) return colors;
    let idx = 0;
    const sorted = [...results.solution.routes].sort((a, b) => {
      if (a.day < b.day) return -1;
      if (a.day > b.day) return 1;
      return (a.technician_name || '').localeCompare(b.technician_name || '');
    });
    for (const r of sorted) {
      const name = r.technician_name;
      if (name && !(name in colors)) {
        colors[name] = ROUTE_COLORS[idx % ROUTE_COLORS.length];
        idx++;
      }
    }
    return colors;
  }, [results]);

  // Build address labels map: matrix index → display string
  // Matrix index 0 = depot, 1..N = addresses (0-based array offset by 1)
  const addressLabels = useMemo(() => {
    const labels = {};
    if (!addresses?.length) return labels;
    addresses.forEach((a, i) => {
      const parts = [a.address || a.street, a.city, a.state, a.zip].filter(Boolean);
      const lcd = a.last_change_date;
      const isLocked = lcd && (Date.now() - new Date(lcd).getTime()) < 90 * 24 * 60 * 60 * 1000;
      labels[String(i + 1)] = {
        text: parts.join(', ') || `Address #${i + 1}`,
        name: a.name || a.customer_name || '',
        tier: a.service_tier || a.tier || '',
        fixedDay: a.required_days || a.req_days || '',
        fixedTech: a.preferred_technician || a.pref_tech || '',
        lockStatus: isLocked ? '\uD83D\uDD12 Locked' : '',
      };
    });
    return labels;
  }, [addresses]);

  const { days, routesByDay, dayStats } = useRouteData(results?.score);
  const currentDay = (activeDay && days.includes(activeDay)) ? activeDay : days[0];
  const currentRoutes = routesByDay[currentDay] || [];

  if (sortedTiers.length === 0) {
    return (
      <div className="ro-panel">
        <div className="ro-empty-state">
          <div className="ro-empty-state-icon">{'\u{1F4CA}'}</div>
          <h4 className="ro-empty-state-title">No Results Yet</h4>
          <p className="ro-empty-state-text">
            Run the optimizer to generate route schedules. Results for each budget tier will appear here.
          </p>
        </div>
      </div>
    );
  }
  if (loading) {
    return (
      <div className="ro-results">
        {/* Skeleton loading state */}
        <div className="ro-score-cards">
          {[1,2,3,4,5].map(i => (
            <div key={i} className="ro-score-card ro-score-card-skeleton">
              <div className="ro-skeleton-value" />
              <div className="ro-skeleton-label" />
            </div>
          ))}
        </div>
        <div className="ro-skeleton-bar" />
        <div className="ro-skeleton-map">Loading results...</div>
      </div>
    );
  }
  if (!results?.solution || !results?.score) {
    return (
      <div className="ro-panel">
        <div className="ro-empty-state">
          <div className="ro-empty-state-icon">{'\u23F3'}</div>
          <h4 className="ro-empty-state-title">No Results for This Tier</h4>
          <p className="ro-empty-state-text">This optimization tier hasn't been run yet. Go back to Optimize and select this budget level.</p>
        </div>
      </div>
    );
  }

  const { solution, score } = results;

  const handleExport = async () => {
    try {
      const result = await api.exportSchedule(weekId);
      if (result.url) window.open(result.url, '_blank');
    } catch (err) {
      console.error('Export failed:', err);
    }
  };

  return (
    <div className="ro-results">
      {/* Tier selector */}
      {sortedTiers.length > 1 && (
        <div className="ro-tier-selector">
          <span className="ro-tier-selector-label">Tier:</span>
          {sortedTiers.map(tier => {
            const isBest = tier === bestTier;
            const isActive = tier === activeTier;
            const cls = ['ro-btn', 'ro-tier-btn'];
            if (isActive) cls.push('ro-tier-btn-active');
            if (isBest) cls.push('ro-tier-btn-best');
            return (
              <button
                key={tier}
                className={cls.join(' ')}
                onClick={() => setActiveTier(tier)}
                title={`Optimization budget: ${TIER_LABELS[tier] || tier}`}
                aria-pressed={isActive}
                aria-label={`${TIER_LABELS[tier] || tier} tier${isBest ? ' (best result)' : ''}`}
              >
                {TIER_LABELS[tier] || tier}
                {isBest && (
                  <span className={`ro-tier-badge-best ${isActive ? 'ro-tier-badge-best-active' : ''}`}>BEST</span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Delta comparison vs previous approved run */}
      {(() => {
        const prevApproved = runHistory.find(r => r.status === 'approved' && r.total_miles);
        if (!prevApproved || !score?.total_distance_miles) return null;
        const prev = Number(prevApproved.total_miles);
        const cur = score.total_distance_miles;
        const diff = cur - prev;
        const pct = prev > 0 ? ((diff / prev) * 100) : 0;
        const prevTechs = prevApproved.active_technicians != null ? Number(prevApproved.active_technicians) : null;
        const curTechs = score.active_technicians;
        const techDiff = prevTechs != null && curTechs != null ? curTechs - prevTechs : null;
        return (
          <div className={`ro-delta-card ${diff <= 0 ? 'ro-delta-card-positive' : 'ro-delta-card-negative'}`}>
            <h4 className="ro-subsection-title">vs Previous Approved</h4>
            <div className="ro-delta-grid">
              <div>
                <div className="ro-delta-label">Previous</div>
                <div className="ro-delta-value">{prev.toFixed(1)} mi</div>
              </div>
              <div>
                <div className="ro-delta-label">Current</div>
                <div className="ro-delta-value ro-delta-value-highlight">{cur.toFixed(1)} mi</div>
              </div>
              <div>
                <div className="ro-delta-label">Change</div>
                <div className={`ro-delta-value ro-delta-change ${diff <= 0 ? 'ro-delta-change-positive' : 'ro-delta-change-negative'}`}>
                  <span className="ro-delta-arrow">{diff < 0 ? '\u25BC' : diff > 0 ? '\u25B2' : ''}</span>
                  {diff <= 0 ? '' : '+'}{diff.toFixed(1)} mi
                  <span className="ro-delta-pct">({pct >= 0 ? '+' : ''}{pct.toFixed(1)}%)</span>
                </div>
              </div>
              {techDiff != null && (
                <div>
                  <div className="ro-delta-label">Employees</div>
                  <div className={`ro-delta-value${techDiff < 0 ? ' ro-delta-change-positive' : techDiff > 0 ? ' ro-delta-change-negative' : ''}`}>
                    {prevTechs} {'\u2192'} {curTechs}
                    {techDiff !== 0 && <span className="ro-delta-pct"> ({techDiff > 0 ? '+' : ''}{techDiff})</span>}
                  </div>
                </div>
              )}
              {prevApproved.changes_count > 0 && (
                <div>
                  <div className="ro-delta-label">Reassigned</div>
                  <div className="ro-delta-value">{prevApproved.changes_count} customers</div>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Infeasibility Warning */}
      {score.feasible === false && (
        <div style={{
          background: '#fdecea', border: '2px solid #d32f2f', borderRadius: '8px',
          padding: '16px 20px', marginBottom: '16px', color: '#b71c1c',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
            <span style={{ fontSize: '28px' }}>{'\u26D4'}</span>
            <strong style={{ fontSize: '18px' }}>Schedule Exceeds Shift Limits</strong>
          </div>
          <p style={{ margin: '0 0 8px 0', fontSize: '14px', lineHeight: 1.5 }}>
            The best solution found has
            <strong> {score.violation_count || 'multiple'} shift overrun{(score.violation_count || 0) !== 1 ? 's' : ''}</strong> where
            employees would need to work beyond their scheduled hours. Days marked
            with <span style={{ fontWeight: 700 }}>{'\u26D4'}</span> exceed 100% utilization.
          </p>
          <p style={{ margin: '0 0 8px 0', fontSize: '13px', lineHeight: 1.5, opacity: 0.85 }}>
            This schedule may still be usable if overtime is acceptable. For a fully compliant schedule,
            consider adding more workdays, increasing shift hours, adding employees, or reducing the number of stops.
          </p>
          {score.violations && score.violations.length > 0 && (
            <details style={{ marginTop: '8px' }}>
              <summary style={{ cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>
                View {score.violation_count || score.violations.length} violation{(score.violation_count || score.violations.length) !== 1 ? 's' : ''}
              </summary>
              <ul style={{ margin: '8px 0 0 0', padding: '0 0 0 20px', fontSize: '12px', maxHeight: '150px', overflow: 'auto' }}>
                {score.violations.map((v, i) => (
                  <li key={i} style={{ marginBottom: '2px' }}>{typeof v === 'string' ? v : JSON.stringify(v)}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}

      {/* Score Summary + Action Buttons */}
      <div className="ro-score-cards">
        <div className="ro-score-card">
          <div className="ro-score-value">{score.total_distance_miles?.toFixed(1)}</div>
          <div className="ro-score-label">Total Miles</div>
        </div>
        <div className="ro-score-card">
          <div className="ro-score-value">{score.active_technicians}</div>
          <div className="ro-score-label">Employees</div>
        </div>
        <div className="ro-score-card">
          <div className="ro-score-value">{score.num_routes}</div>
          <div className="ro-score-label">Routes</div>
        </div>
        <div className="ro-score-card">
          <div className="ro-score-value">{score.total_stops}</div>
          <div className="ro-score-label">Stops</div>
        </div>
        <div className="ro-score-card">
          <div className="ro-score-value">
            {score.num_routes > 0 ? (score.total_distance_miles / score.num_routes).toFixed(1) : '—'}
          </div>
          <div className="ro-score-label">Avg mi/route</div>
        </div>
        <div className="ro-score-card ro-score-action">
          <button className="ro-btn ro-btn-primary ro-score-action-btn" onClick={() => window.print()} aria-label="Print route schedule to PDF">
            Print to PDF
          </button>
          <button className="ro-btn ro-score-action-btn" onClick={handleExport} aria-label="Download schedule as Excel file">
            Export to Excel
          </button>
        </div>
      </div>

      {/* Per-technician summary */}
      {solution.routes?.length > 0 && (() => {
        // Build a lookup from score.per_route (which has distance_miles)
        const perRouteMap = {};
        (score.per_route || []).forEach(pr => {
          const key = `${pr.day}_${pr.technician || pr.technician_name}`;
          perRouteMap[key] = pr;
        });

        const techMap = {};
        solution.routes.forEach(r => {
          const name = r.technician_name || 'Unknown';
          if (!techMap[name]) techMap[name] = { stops: 0, miles: 0, days: new Set() };
          techMap[name].stops += (r.stops || r.sequence || []).length;
          // Get miles from score.per_route (solution.routes doesn't have distance_miles)
          const prKey = `${r.day}_${name}`;
          const pr = perRouteMap[prKey];
          techMap[name].miles += (pr?.distance_miles || r.distance_miles || 0);
          techMap[name].days.add(r.day);
        });
        const techs = Object.entries(techMap)
          .map(([name, s]) => ({ name, stops: s.stops, miles: s.miles, days: s.days.size }))
          .sort((a, b) => b.miles - a.miles);
        return (
          <details className="ro-tech-summary-details">
            <summary className="ro-subsection-title" style={{ cursor: 'pointer', userSelect: 'none' }}>
              Employee Summary ({techs.length} active)
            </summary>
            <div className="ro-tech-summary-table-wrap">
              <table className="ro-tech-summary-table">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Days</th>
                    <th>Stops</th>
                    <th>Miles</th>
                    <th>Avg mi/day</th>
                  </tr>
                </thead>
                <tbody>
                  {techs.map(t => (
                    <tr key={t.name}>
                      <td>{t.name}</td>
                      <td>{t.days}</td>
                      <td>{t.stops}</td>
                      <td>{t.miles.toFixed(1)}</td>
                      <td>{t.days > 0 ? (t.miles / t.days).toFixed(1) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        );
      })()}

      {/* Service tier distribution */}
      {addresses?.length > 0 && (() => {
        const tiers = {};
        addresses.forEach(a => {
          const t = (a.service_tier || a.tier || 'standard').toLowerCase();
          tiers[t] = (tiers[t] || 0) + 1;
        });
        const entries = Object.entries(tiers).sort((a, b) => b[1] - a[1]);
        return entries.length > 1 ? (
          <div className="ro-tag-row ro-tier-distribution">
            <span className="ro-tier-distribution-label">Service tiers:</span>
            {entries.map(([t, count]) => (
              <span key={t} className="ro-tag">{t} ({count})</span>
            ))}
          </div>
        ) : null;
      })()}

      {/* Day Tabs */}
      <DayTabs
        days={days}
        dayStats={dayStats}
        currentDay={currentDay}
        onSelectDay={(day) => { setActiveDay(day); setSelectedRoute(null); }}
      />

      {/* Per-day summary stats */}
      {currentDay && dayStats[currentDay] && (
        <div className="ro-day-stats">
          <div className="ro-score-card ro-score-card-sm">
            <div className="ro-score-value">{dayStats[currentDay].miles?.toFixed(1) || 0}</div>
            <div className="ro-score-label">{currentDay} Miles</div>
          </div>
          <div className="ro-score-card ro-score-card-sm">
            <div className="ro-score-value">{dayStats[currentDay].count || 0}</div>
            <div className="ro-score-label">{currentDay} Routes</div>
          </div>
          <div className="ro-score-card ro-score-card-sm">
            <div className="ro-score-value">{dayStats[currentDay].stops || 0}</div>
            <div className="ro-score-label">{currentDay} Stops</div>
          </div>
          <div className="ro-score-card ro-score-card-sm">
            <div className="ro-score-value">{dayStats[currentDay].avgUtil != null ? `${dayStats[currentDay].avgUtil.toFixed(0)}%` : '\u2014'}</div>
            <div className="ro-score-label">{currentDay} Utilization</div>
          </div>
        </div>
      )}

      {/* 2-column: route list sidebar + map */}
      <div className="ro-results-layout">
        <div className="ro-results-sidebar">
          <RouteCards
            routes={currentRoutes}
            currentDay={currentDay}
            techColors={techColors}
            selectedRoute={selectedRoute}
            onSelectRoute={setSelectedRoute}
          />
        </div>

        <div className="ro-results-map">
          <MapViewer
            solution={solution}
            coords={coords}
            geometries={geometries}
            selectedRoute={selectedRoute}
            techColors={techColors}
            activeDay={currentDay}
            addressLabels={addressLabels}
            settings={settings}
          />
        </div>
      </div>

      {/* Itinerary Table */}
      <ItineraryTable
        weekId={weekId}
        selectedRoute={selectedRoute}
        coords={coords}
        solution={solution}
        settings={settings}
        addressLabels={addressLabels}
      />

      {/* Proceed to Approve */}
      {onProceed && (
        <div className="ro-section-divider">
          <button className="ro-btn ro-btn-primary ro-btn-lg" onClick={onProceed}>
            Proceed to Approval
          </button>
        </div>
      )}
    </div>
  );
}
